/**
 * 运行期主题状态 —— 一个极小的订阅式 store。
 *
 * 为什么不用 initialState：主题是「点一下要立刻见效」的高频交互，而写 initialState
 * 会让**整棵 @@initialState 树重渲染**（33 个消费方，其中 umi 的 layout 运行时一重跑，
 * ProLayout 连同内容区一起重建）。那是切主题卡顿的主因之一。
 * 这个 store 只通知真正消费主题的组件，其余页面一动不动。
 *
 * 职责边界（刻意拆开，避免判据分叉）：
 *   · 判据与落盘  → utils/theme.ts（readStoredDarkMode / persistDarkMode）
 *   · 运行期广播  → 本文件（publishDarkMode / useDarkMode）
 * 本文件**不自己写 localStorage、也不自己碰 <html data-theme>**：
 * 落盘一律走 persistDarkMode，它同时同步根属性，CSS 变量面因此在同一帧翻转。
 *
 * 消费方：src/app.tsx（navTheme + ProLayout token）、TabLayout（图标与容器底色）、
 *   components/Footer、pages/dashboard/useDashboardDarkMode（看板图表取色）。
 */

import { useSyncExternalStore } from 'react';
import { readStoredDarkMode } from './theme';

let current = readStoredDarkMode();

const listeners = new Set<() => void>();

/** 当前主题（非响应式读法，供事件回调/工具函数使用） */
export function getDarkMode(): boolean {
  return current;
}

export function subscribeDarkMode(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * 广播主题变化。**只广播，不落盘** —— 调用方负责先用 persistDarkMode 落盘 + 同步根属性，
 * 再调它；这样「属性翻转」与「React 提交」的顺序仍由调用方一处掌控（见 TabLayout.toggleTheme）。
 */
export function publishDarkMode(dark: boolean): void {
  if (dark === current) return;
  current = dark;
  listeners.forEach((listener) => { listener(); });
}

/** 订阅主题（组件侧唯一入口） */
export function useDarkMode(): boolean {
  return useSyncExternalStore(subscribeDarkMode, getDarkMode, getDarkMode);
}

export default {
  getDarkMode,
  subscribeDarkMode,
  publishDarkMode,
  useDarkMode,
};